import { requireLanguage, requireInferenceText, requestInference, parseInferenceResult, pickLanguageVoice } from "./languageContract.js";

export const DEFAULT_MODEL = "auto";

export function validateSourceText(text) {
    if (typeof text !== "string" || !text.trim()) throw new Error("Enter text before continuing.");
    if (text.length > 4000) throw new Error("Text is limited to 4,000 characters. Shorten it and retry.");
    return text;
}

export const resolveLanguage = (language, customLanguage = "") =>
    requireLanguage(language === "+ Add New Language" ? customLanguage : language);

export const asrHint = (language, dialect) => `${requireLanguage(language)}|||${dialect}`;

export async function translateText(app, text, sourceLanguage, sourceDialect, targetLanguage, targetDialect) {
    validateSourceText(text);
    return (await requestInference(app, "/translate_peer_v3", [
        text, requireLanguage(sourceLanguage), sourceDialect,
        requireLanguage(targetLanguage), targetDialect, DEFAULT_MODEL,
    ])).text;
}

export async function translateNative(appFactory, url, text, sourceLanguage, sourceDialect, targetLanguage, targetDialect) {
    validateSourceText(text);
    const payload = {
        text, source_language: requireLanguage(sourceLanguage), source_dialect: sourceDialect,
        target_language: requireLanguage(targetLanguage), target_dialect: targetDialect,
        model_choice: DEFAULT_MODEL,
    };
    try {
        const response = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error("Native translation unavailable.");
        return parseInferenceResult(await response.json()).text;
    } catch (_) {
        // The only fallback has the same full language and verified-success contract.
        return translateText(await appFactory(), text, sourceLanguage, sourceDialect, targetLanguage, targetDialect);
    }
}

export async function clarifyText(app, text, language, dialect) {
    validateSourceText(text);
    const result = await requestInference(app, "/generate_clarifications_v3", [text, requireLanguage(language), dialect, DEFAULT_MODEL]);
    return { data: [JSON.stringify({ clarification: result.text })], inference: result };
}

export async function replyText(app, text, meaning, sourceLanguage, sourceDialect, targetLanguage, targetDialect) {
    validateSourceText(text);
    if (meaning) validateSourceText(meaning);
    return (await requestInference(app, "/generate_reply_v3", [
        text, meaning, requireLanguage(sourceLanguage), sourceDialect,
        requireLanguage(targetLanguage), targetDialect, DEFAULT_MODEL,
    ])).text;
}

export function speakSelectedLanguage(text, language, dialect) {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) return false;
    const { profile, voice } = pickLanguageVoice(window.speechSynthesis.getVoices(), language, dialect);
    if (!profile) return false;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = profile.lang;
    utterance.rate = profile.rate;
    utterance.pitch = profile.pitch;
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
    return true;
}

export function validMeaning(value) {
    try { requireInferenceText(value); return true; } catch (_) { return false; }
}

export function mergeLanguageCatalog(previous, incoming) {
    if (!incoming || typeof incoming !== "object" || Array.isArray(incoming)) throw new Error("Invalid language catalog.");
    const entries = Object.entries(incoming).filter(([language, dialects]) =>
        !language.startsWith("+") && Array.isArray(dialects));
    const substantive = entries.some(([language, dialects]) => language !== "Other" &&
        dialects.some(dialect => typeof dialect === "string" && !dialect.startsWith("+") && !/^test dialect$/i.test(dialect)));
    if (!substantive) throw new Error("Language catalog is empty or test-only. Retaining saved choices.");
    const merged = { ...previous };
    for (const [language, dialects] of entries) {
        merged[language] = [...new Set([...(previous?.[language] || []), ...dialects].filter(d => typeof d === "string" && !/^test dialect$/i.test(d) && !d.startsWith("+"))), "+ Add New Dialect"];
    }
    merged["+ Add New Language"] = ["+ Add New Dialect"];
    return merged;
}
